import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { useLanguage } from "../language/LanguageContext";
import profileImg from "../public/assets/profileImg2.jpg";
import SocialLinks, { LINKEDIN_URL } from "./ui/SocialLinks";

/**
 * Grid placement lets one set of nodes serve two arrangements:
 *
 *   mobile                        desktop
 *   ┌──────┬───────────┐          ┌───────────────┬─────┐
 *   │ img  │ name      │          │ name          │ img │
 *   ├──────┴───────────┤          ├───────────────┤     │
 *   │ meta + actions   │          │ meta + actions│     │
 *   └──────────────────┘          └───────────────┴─────┘
 *
 * The portrait sits beside the name only, so the CTAs below keep full width.
 */
const HeroSection = () => {
  const { translations } = useLanguage();
  const { hero, about } = translations;

  return (
    <header className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[46rem] h-[30rem] rounded-full opacity-[0.18] blur-3xl bg-accent-gradient"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-dark-border to-transparent"
      />

      <section className="relative max-w-5xl mx-auto px-5 pt-12 md:pt-24 pb-12 md:pb-20">
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-5 md:grid-cols-[1fr_auto] md:gap-x-10 md:items-center">
          {/* Portrait — beside the name on mobile, its own column on desktop */}
          <div className="col-start-1 row-start-1 self-center md:col-start-2 md:row-start-1 md:row-span-2 md:self-center">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-40 md:h-40">
              <div
                aria-hidden="true"
                className="absolute -inset-1 rounded-2xl bg-accent-gradient opacity-40 blur-md"
              />
              <img
                src={profileImg}
                alt="Renan Ferreira"
                width={160}
                height={160}
                loading="eager"
                decoding="async"
                className="relative w-full h-full object-cover rounded-2xl ring-1 ring-white/10"
              />
            </div>
          </div>

          {/* Name + subtitle */}
          <div className="col-start-2 row-start-1 min-w-0 self-center md:col-start-1 md:row-start-1">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold mb-1.5 md:mb-3 leading-[1.05] tracking-tight">
              <span className="text-gradient">{hero.name}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-dark-text font-medium">
              {hero.subtitle}
            </p>
          </div>

          {/* Meta + actions */}
          <div className="col-span-2 md:col-span-1 md:col-start-1 md:row-start-2">
            <p className="text-sm md:text-base text-dark-muted mb-4">{hero.jobTitle}</p>

            <div className="flex items-center gap-2 text-sm text-dark-muted mb-7">
              <MapPin size={15} />
              <span>{about.location}</span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href="#contact"
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-dark-bg bg-accent-gradient transition duration-300 ease-out-soft hover:shadow-glow hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg"
              >
                {hero.contactButton}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </a>

              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-dark-text border border-dark-border bg-dark-surface transition duration-300 ease-out-soft hover:-translate-y-0.5 hover:text-dark-heading hover:border-dark-border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dark-bg"
              >
                {hero.linkedinButton}
              </a>

              <SocialLinks size={18} className="ml-1" />
            </div>
          </div>
        </div>
      </section>
    </header>
  );
};

export default HeroSection;
